/**
 * Runtime configuration reader.
 *
 * `process.env.REACT_APP_*` is inlined by CRA at build time, so it cannot be changed
 * once the image is built. Values are therefore resolved in this order:
 *
 *   1. `window.__ENV__` — written by docker-entrypoint.sh into env.js at container start
 *   2. `process.env.REACT_APP_*` — build-time value, kept as fallback
 *   3. the hard-coded default in appconfig
 *
 * That ordering means the same bundle works in a container (runtime value) and under
 * `npm start` (build-time value), with no separate code path for either.
 */

declare global {
  interface Window {
    __ENV__?: Record<string, string | undefined>;
  }
}

/**
 * Returns the runtime override for `key`, or undefined when it is absent, blank, or
 * still an unsubstituted placeholder. Blank counts as absent so that an env var set to
 * the empty string does not shadow the build-time fallback with "".
 */
export function runtimeEnv(key: string): string | undefined {
  // Guard the whole access: env.js may be missing (older image, direct file open) and
  // window is undefined under server-side rendering or a jsdom-less test runner.
  try {
    if (typeof window === "undefined") return undefined;

    const value = window.__ENV__?.[key];
    if (typeof value !== "string") return undefined;

    const trimmed = value.trim();
    if (trimmed === "") return undefined;

    return trimmed;
  } catch {
    return undefined;
  }
}

/**
 * Normalises a build-time `process.env.REACT_APP_*` value: returns undefined when it is
 * absent OR blank, so `??` continues down the chain to the hard-coded default.
 *
 * This is not defensive noise. The Dockerfile declares `ENV REACT_APP_X=$REACT_APP_X`
 * for every override, so when the matching build-arg is not passed the variable is
 * *defined and empty* rather than absent, and CRA inlines it as "". Plain
 * `process.env.REACT_APP_X ?? fallback` then evaluates to "" — `??` only falls back on
 * null/undefined — leaving the URL empty and sending requests to the frontend's own
 * origin instead of the API.
 */
export function buildTimeEnv(value: string | undefined): string | undefined {
  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * Resolves one configuration value: runtime env.js, then the build-time value, then the
 * hard-coded fallback.
 *
 * `buildTimeValue` must be passed as the literal `process.env.REACT_APP_X` expression —
 * CRA substitutes it at build time by matching that exact source text, so reading it
 * through a variable or a computed key would yield undefined.
 *
 * @param key         the REACT_APP_* name, looked up in window.__ENV__ at runtime
 * @param buildTimeValue  literally `process.env.REACT_APP_<KEY>`
 * @param fallback    value used when neither source supplies one
 */
export function configValue(
  key: string,
  buildTimeValue: string | undefined,
  fallback: string
): string {
  return runtimeEnv(key) ?? buildTimeEnv(buildTimeValue) ?? fallback;
}
